/**
 * AycanCoasterPicker — Web Component
 * Tek grid: müşteri tasarımları tıklama sırasıyla seçer, ardışık ikili
 * gruplar bir "çift" oluşturur. Qty = aynı tasarımın N ardışık tıklaması.
 * Sepet: her çift → { id: pair[0].variantId, quantity: 1, properties: { 'Pattern 2': pair[1].code } }
 */
class AycanCoasterPicker extends HTMLElement {
  connectedCallback() {
    this.variantMap = JSON.parse(this.querySelector('.aycan-variant-map').textContent);
    this.selection = []; // ordered by click sequence: { code, variantId, qty }
    this._lastClickedCode = null;

    this.atcBtn       = this.querySelector('.aycan-coaster__atc');
    this.errorEl      = this.querySelector('.aycan-coaster__error');
    this.clearBtn     = this.querySelector('.aycan-coaster__clear');
    this.spinner      = this.atcBtn?.querySelector('.loading__spinner');
    this.atcLabel     = this.atcBtn?.querySelector('.aycan-atc__label');
    this._defaultAtcLabel = this.atcLabel?.textContent?.trim() || '';
    this._pairIncompleteLabel = this.dataset.pairIncompleteLabel || 'Add one more design to complete the pair';

    this._bindEvents();
    this._initSwatchImageFallback();
    this._validate();
  }

  _getAtcLabels() {
    const dock = document.getElementById(`pdp-mobile-dock-${this.dataset.sectionId}`);
    return {
      selectLabel: dock?.dataset.selectLabel || 'Select designs',
      defaultLabel: this._defaultAtcLabel || dock?.dataset.atcLabel || 'Add to cart',
    };
  }

  _scrollToPicker() {
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const first = this.querySelector('.aycan-swatch__btn:not([disabled])');
    first?.focus({ preventScroll: true });
  }

  _applyBuyboxAtc(totalUnits, isValid) {
    if (!this.atcBtn) return;
    const { selectLabel, defaultLabel } = this._getAtcLabels();
    if (totalUnits === 0) {
      this.atcBtn.disabled = false;
      this.atcBtn.classList.add('is-select-mode');
      if (this.atcLabel) this.atcLabel.textContent = selectLabel;
      return;
    }
    this.atcBtn.classList.remove('is-select-mode');
    if (this.atcLabel) this.atcLabel.textContent = isValid ? defaultLabel : this._pairIncompleteLabel;
    this.atcBtn.disabled = !isValid;
  }

  _bindEvents() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('.aycan-swatch__qty, .aycan-qty__minus, .aycan-qty__plus, .aycan-qty__input')) {
        return;
      }
      const btn = event.target.closest('.aycan-swatch__btn');
      if (!btn || btn.disabled || !this.contains(btn)) return;
      const item = btn.closest('.aycan-swatch-item');
      if (!item) return;
      this._toggleSwatch(item);
    });

    this.querySelectorAll('.aycan-qty__minus').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        this._adjustQty(btn.closest('.aycan-swatch-item'), -1);
      });
    });

    this.querySelectorAll('.aycan-qty__plus').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        this._adjustQty(btn.closest('.aycan-swatch-item'), 1);
      });
    });

    this.querySelectorAll('.aycan-qty__input').forEach((input) => {
      input.addEventListener('click', (event) => event.stopPropagation());
      input.addEventListener('change', () => {
        const item = input.closest('.aycan-swatch-item');
        if (!item) return;
        const val = Math.max(1, Math.min(99, parseInt(input.value, 10) || 1));
        input.value = val;
        this._syncItemQty(item, val);
      });
    });

    this.atcBtn?.addEventListener('click', () => {
      if (this.selection.length === 0) {
        this._scrollToPicker();
        return;
      }
      this._addToCart();
    });

    this.clearBtn?.addEventListener('click', () => {
      this._resetSelection();
    });
  }

  _getItemQty(item) {
    const input = item.querySelector('.aycan-qty__input');
    return input ? Math.max(1, Math.min(99, parseInt(input.value, 10) || 1)) : 1;
  }

  _syncItemQty(item, qty) {
    const code = item.dataset.code;
    const entry = this.selection.find((x) => x.code === code);
    if (entry) entry.qty = qty;
    this._updateCounts();
    this._validate();
  }

  _adjustQty(item, delta) {
    if (!item) return;
    const input = item.querySelector('.aycan-qty__input');
    if (!input) return;
    const val = Math.max(1, Math.min(99, (parseInt(input.value, 10) || 1) + delta));
    input.value = val;
    this._syncItemQty(item, val);
  }

  _setQtyVisible(item, visible) {
    const qtyEl = item.querySelector('.aycan-swatch__qty');
    if (!qtyEl) return;
    const qtyInputs = qtyEl.querySelectorAll('.aycan-qty__input, .aycan-qty__minus, .aycan-qty__plus');
    qtyEl.hidden = !visible;
    qtyEl.setAttribute('aria-hidden', visible ? 'false' : 'true');
    qtyInputs.forEach((el) => {
      el.tabIndex = visible ? 0 : -1;
    });
    if (!visible) {
      const input = qtyEl.querySelector('.aycan-qty__input');
      if (input) input.value = 1;
    }
  }

  _toggleSwatch(item) {
    const code = item.dataset.code;
    const idx  = this.selection.findIndex(x => x.code === code);
    const isNowSelected = idx === -1;

    item.classList.toggle('aycan-swatch-item--selected', isNowSelected);
    item.querySelector('.aycan-swatch__btn').setAttribute('aria-pressed', String(isNowSelected));

    if (isNowSelected) {
      const qty = this._getItemQty(item);
      this.selection.push({ code, variantId: parseInt(item.dataset.variantId, 10), qty });
      this._setQtyVisible(item, true);
      this._lastClickedCode = code;
    } else {
      this.selection.splice(idx, 1);
      this._setQtyVisible(item, false);
    }

    this._updateCounts();
    this._validate();
  }

  // Seçilen tasarımları qty kadar tekrarlayıp tıklama sırasına göre
  // düzleştirilmiş bir birim listesi üretir.
  _flattenUnits() {
    const units = [];
    for (const entry of this.selection) {
      for (let i = 0; i < (entry.qty || 1); i++) {
        units.push({ code: entry.code, variantId: entry.variantId });
      }
    }
    return units;
  }

  // Birim listesini ardışık ikişerli gruplar.
  _computePairs() {
    const units = this._flattenUnits();
    const pairs = [];
    for (let i = 0; i + 1 < units.length; i += 2) {
      pairs.push([units[i], units[i + 1]]);
    }
    return { pairs, totalUnits: units.length, isOdd: units.length % 2 === 1 };
  }

  _updateCounts() {
    const { pairs, totalUnits } = this._computePairs();
    const countEl = this.querySelector('.aycan-coaster__count');
    if (countEl) {
      const n = pairs.length;
      countEl.textContent = `${n} pair${n === 1 ? '' : 's'} formed`;
      countEl.classList.toggle('is-empty', n === 0);
      countEl.setAttribute('aria-hidden', n === 0 ? 'true' : 'false');
    }
    if (this.clearBtn) this.clearBtn.hidden = totalUnits === 0;
  }

  _validate() {
    const { pairs, totalUnits, isOdd } = this._computePairs();
    const isValid = pairs.length > 0 && !isOdd;

    this._applyBuyboxAtc(totalUnits, isValid);
    this._emitDockUpdate(isValid, pairs.length, totalUnits);
  }

  _computeCoasterTotalCents() {
    return this._flattenUnits().reduce((sum, unit) => {
      const el = this.querySelector(
        `.aycan-swatch-item[data-code="${CSS.escape(unit.code)}"]`
      );
      const price = parseInt(el?.dataset.price, 10) || 0;
      return sum + price;
    }, 0);
  }

  _getDockThumb() {
    const el = this._lastClickedCode
      ? this.querySelector(`.aycan-swatch-item[data-code="${CSS.escape(this._lastClickedCode)}"]`)
      : null;
    const img = el?.querySelector('.aycan-swatch__btn--image img');
    if (img?.currentSrc || img?.src) return img.currentSrc || img.src;
    const dock = document.getElementById(`pdp-mobile-dock-${this.dataset.sectionId}`);
    const thumb = dock?.querySelector('.pdp-mobile-dock__thumb');
    return thumb?.tagName === 'IMG' ? thumb.src : '';
  }

  _emitDockUpdate(isValid, pairCount, totalUnits) {
    if (typeof window.pdpDockUpdate !== 'function') return;

    const computed = this._computePairs();
    const g1 = pairCount ?? computed.pairs.length;
    const units = totalUnits ?? computed.totalUnits;
    const hasItems = units > 0;
    const dock = document.getElementById(`pdp-mobile-dock-${this.dataset.sectionId}`);
    const selectLabel = dock?.dataset.selectLabel || 'Select designs';
    const defaultLabel = this._defaultAtcLabel || dock?.dataset.atcLabel || 'Add to cart';
    const valid = isValid ?? (g1 > 0 && units % 2 === 0);

    window.pdpDockUpdate(this.dataset.sectionId, {
      thumbSrc: this._getDockThumb(),
      selectionLine: hasItems
        ? `${g1} pair${g1 === 1 ? '' : 's'} · ${this._formatMoney(this._computeCoasterTotalCents())}`
        : '',
      ctaLabel: !hasItems ? selectLabel : (valid ? defaultLabel : this._pairIncompleteLabel),
      ctaDisabled: hasItems && !valid,
      ctaMode: !hasItems ? 'scroll' : 'add',
    });
  }

  _formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return `$${(cents / 100).toFixed(2)}`;
  }

  async _addToCart() {
    const { pairs, isOdd } = this._computePairs();
    if (pairs.length === 0 || isOdd) return;

    const items = pairs.map(([a, b]) => ({
      id:         a.variantId,
      quantity:   1,
      properties: { 'Pattern 2': b.code },
    }));

    this._setLoading(true);

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      let sections = ['cart-icon-bubble'];
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        sections = cartDrawer.getSectionsToRender().map((s) => s.id);
      }

      const resp = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ items, sections, sections_url: window.location.pathname }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        throw new Error(data.description || 'Could not add to cart.');
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source:   'aycan-coaster-picker',
        cartData: data,
      });

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
      }

      this._resetSelection();
    } catch (err) {
      if (this.errorEl) {
        this.errorEl.textContent = err.message;
        this.errorEl.hidden = false;
      }
    } finally {
      this._setLoading(false);
    }
  }

  _setLoading(isLoading) {
    if (this.atcBtn)   this.atcBtn.disabled   = isLoading;
    this.spinner?.classList.toggle('hidden', !isLoading);
    this.atcLabel?.classList.toggle('hidden', isLoading);
    window.pdpDockUpdate?.(this.dataset.sectionId, { loading: isLoading });
    if (!isLoading) this._validate();
  }

  _resetSelection() {
    this.selection = [];
    this._lastClickedCode = null;
    this.querySelectorAll('.aycan-swatch-item').forEach(item => {
      item.classList.remove('aycan-swatch-item--selected');
      item.querySelector('.aycan-swatch__btn').setAttribute('aria-pressed', 'false');
      this._setQtyVisible(item, false);
    });
    this._updateCounts();
    this._validate();
    if (this.errorEl) this.errorEl.hidden = true;
  }

  _initSwatchImageFallback() {
    this.querySelectorAll('.aycan-swatch__btn--image img').forEach(img => {
      img.addEventListener('error', () => {
        const btn = img.closest('.aycan-swatch__btn');
        const figure = img.closest('.aycan-swatch__figure');
        btn.classList.remove('aycan-swatch__btn--image');
        figure?.remove();
        img.remove();
        btn.textContent = btn.dataset.fallback || '';
      }, { once: true });
    });
  }
}

customElements.define('aycan-coaster-picker', AycanCoasterPicker);
